/**
 * Toast Container Component
 * Renders toast notifications in a fixed position
 */

import { type Toast, type ToastType, useToast } from '@contexts/ToastContext';
import { AlertCircle, AlertTriangle, Check, Info, X } from 'lucide-react';
import type { ReactElement } from 'react';

// =============================================================================
// Helpers
// =============================================================================

function getToastStyles(type: ToastType): { colors: string; icon: ReactElement } {
  switch (type) {
    case 'error':
      return {
        colors: 'bg-theme-danger text-theme-text-inverse',
        icon: <AlertCircle className="w-5 h-5" />,
      };
    case 'success':
      return {
        colors: 'bg-theme-success text-theme-text-inverse',
        icon: <Check className="w-5 h-5" />,
      };
    case 'warning':
      return {
        colors: 'bg-theme-warning text-theme-text-inverse',
        icon: <AlertTriangle className="w-5 h-5" />,
      };
    default:
      return {
        colors: 'bg-theme-primary-fill text-theme-on-primary-fill',
        icon: <Info className="w-5 h-5" />,
      };
  }
}

// =============================================================================
// Toast Item Component
// =============================================================================

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps): ReactElement {
  const styles = getToastStyles(toast.type);

  return (
    <div
      className={`${styles.colors} px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 min-w-[300px] max-w-md animate-slide-in`}
      role="alert"
      data-testid={`toast-${toast.type}`}
    >
      <div className="shrink-0">{styles.icon}</div>
      <p className="flex-1 text-sm">{toast.message}</p>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="shrink-0 p-1 hover:bg-theme-text-inverse/20 rounded transition"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// =============================================================================
// Container Component
// =============================================================================

export function ToastContainer(): ReactElement | null {
  const { toasts, dismissToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
      ))}
    </div>
  );
}
